import type { SearchEntry } from './search'
import type { DeviceCategory, InventoryStatus, PortDirection, PowerDrawSource, PowerSource, SignalCategory, SlotBus } from './signals'

export type PortDTO = {
  id: string
  name: string
  direction: PortDirection
  connector: string
  signal: SignalCategory
  position: number
  /** Slot this port was copied from when a card was installed; null = onboard. */
  slotId: string | null
  /**
   * Cable runs already drawn on this port. `ProjectEdge` cascades from `Port`,
   * so this is exactly what removing the port would delete.
   *
   * Undefined means nobody asked, not zero — only the single-device GETs count
   * it. Anything acting on the number has to treat absence as "unknown".
   */
  cableRuns?: number
}

export type DeviceSlotDTO = {
  id: string
  label: string
  bus: SlotBus
  position: number
  cardId: string | null
  /** The installed card template, if any. */
  card: { id: string; manufacturer: string; model: string; name: string } | null
}

export type DeviceDTO = {
  id: string
  /** null = shared catalogue entry; otherwise the owning workspace. */
  workspaceId: string | null
  name: string
  manufacturer: string
  model: string
  formFactorRU: number
  ipAddress: string | null
  notes: string | null
  sourceUrl: string | null
  verified: boolean
  /** When the ports were last checked against the spec sheet; null when unverified. */
  verifiedAt: string | null
  /** Who checked them. Only the owning workspace sees the email; catalogue rows expose null. */
  verifiedBy: string | null
  category: DeviceCategory
  /** Basic specs; only set on computers. */
  cpu: string | null
  ram: string | null
  gpu: string | null
  storage: string | null
  os: string | null
  /** Operating draw in watts; null when nothing is published. */
  powerDrawTypicalW: number | null
  /** Maximum draw in watts. A lone published figure lives here. */
  powerDrawMaxW: number | null
  /** PSU nameplate — never a draw figure. */
  powerSupplyRatedW: number | null
  powerSource: PowerSource
  powerDrawSource: PowerDrawSource
  powerNote: string | null
  poePdDrawW: number | null
  poeType: number | null
  poeBudgetW: number | null
  mainsRatingA: number | null
  /** Expansion slots; empty for everything but computers. */
  slots: DeviceSlotDTO[]
  /** How many computer slots have this device installed as their card. */
  installedInCount: number
  ports: PortDTO[]
}

/** A manufacturer as the brand strip shows it. `id` is null until an admin has
 *  uploaded a logo (or recorded a website) — lettermark only until then. */
export type BrandDTO = {
  id: string | null
  name: string
  website: string | null
  hasLogo: boolean
  /** Immutable, hash-named URL (/api/brands/<id>/<hash>.<ext>); null without a logo. */
  logoUrl: string | null
}

export type CanvasNodeDTO = {
  id: string
  deviceId: string
  label: string | null
  x: number
  y: number
  /** Physical unit from the workspace inventory assigned to this placement. */
  inventoryItemId: string | null
}

/**
 * What the canvas palette needs to list a piece of equipment: its identity,
 * its category, and the shape of its connectors as counts rather than as
 * every row. The full DeviceDTO for the whole catalogue is about a megabyte
 * once every port is included; this is about seventy kilobytes, and the extra
 * detail is only needed for a device once it is actually on the canvas.
 */
export type DevicePaletteDTO = DeviceOption & {
  formFactorRU: number
  inputs: number
  outputs: number
  bidirectional: number
  slots: number
  filledSlots: number
  /** Precomputed on the server; see searchIndexFrom in lib/search.ts. */
  search: SearchEntry
}

/**
 * What a picker needs to name a piece of equipment: no ports, no slots, no
 * power figures. Derived from DeviceDTO so it cannot drift from it.
 */
export type DeviceOption = Pick<
  DeviceDTO,
  'id' | 'workspaceId' | 'name' | 'manufacturer' | 'model' | 'category'
>

export type InventoryItemDTO = {
  id: string
  deviceId: string
  assetTag: string | null
  serialNumber: string | null
  status: InventoryStatus
  location: string | null
  notes: string | null
  updatedAt: string
}

/** "Camera 1 (Blackmagic Studio Camera 6K Pro)" or just the device name. */
export function nodeDisplayName(label: string | null, deviceName: string): string {
  return label?.trim() ? label.trim() : deviceName
}

/** Asset tag, else serial, else a short id — whatever identifies a unit on site. */
export function unitLabel(item: { assetTag: string | null; serialNumber: string | null; id: string }): string {
  return item.assetTag || item.serialNumber || `unit ${item.id.slice(-4)}`
}

export type CanvasEdgeDTO = {
  id: string
  sourceNodeId: string
  sourcePortId: string
  targetNodeId: string
  targetPortId: string
  label: string | null
  /** Run length in metres, as the cable is actually pulled. Null = not measured. */
  lengthM: number | null
  /** Coax grade or HDMI certification; null takes the sensible default. */
  cableGrade: string | null
  /** Overrides the show format for this run only. */
  formatOverride: string | null
  /** The catalogue cable this run uses, when it names one. */
  cableId: string | null
}

export type ProjectSummaryDTO = {
  id: string
  name: string
  description: string | null
  updatedAt: string
  nodeCount: number
  edgeCount: number
}

export type ProjectDetailDTO = {
  id: string
  name: string
  description: string | null
  /** The format the whole show runs at, e.g. "1080p50". Null until set. */
  showFormat: string | null
  nodes: CanvasNodeDTO[]
  edges: CanvasEdgeDTO[]
}

/** Onboard ports and, per slot, the copies of the installed card's ports. */
export function partitionPorts(device: { ports: PortDTO[] }) {
  const onboard: PortDTO[] = []
  const bySlot = new Map<string, PortDTO[]>()
  for (const port of device.ports) {
    if (!port.slotId) {
      onboard.push(port)
      continue
    }
    const bucket = bySlot.get(port.slotId)
    if (bucket) bucket.push(port)
    else bySlot.set(port.slotId, [port])
  }
  return { onboard, bySlot }
}

/** Splits a device's ports into the sections a canvas node renders. */
export function splitPorts(ports: PortDTO[]) {
  const byPosition = (a: PortDTO, b: PortDTO) => a.position - b.position
  return {
    inputs: ports.filter((p) => p.direction === 'INPUT').sort(byPosition),
    outputs: ports.filter((p) => p.direction === 'OUTPUT').sort(byPosition),
    bidirectional: ports.filter((p) => p.direction === 'BIDIRECTIONAL').sort(byPosition),
  }
}

/**
 * A bi-directional port needs two React Flow handles on one connector — a
 * target on the left and a source on the right — and handle ids must be unique
 * within a node. They are suffixed, and stripped again when saving, so the
 * database only ever stores the real port id.
 */
export function handleIdFor(port: PortDTO, role: 'source' | 'target'): string {
  if (port.direction !== 'BIDIRECTIONAL') return port.id
  return `${port.id}::${role === 'source' ? 'out' : 'in'}`
}

export function portIdFromHandle(handleId: string): string {
  const marker = handleId.indexOf('::')
  return marker === -1 ? handleId : handleId.slice(0, marker)
}

/**
 * Ports in a numbered run share a group ("SDI In 1".."SDI In 20" -> "SDI In"),
 * which is what the node collapses to keep a 120x120 router usable.
 */
export function portGroupName(port: PortDTO): string {
  return port.name.replace(/\s+\d+$/, '').trim() || port.name
}
